import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { client } from './client';
import type { Mailbox, StaffMember } from '../types';

export interface CreateMailboxDto {
  /** Кому принадлежит ящик. Оператору поле недоступно: сервер ставит его самого */
  ownerId?: string;
  email: string;
  name: string;
  imapHost: string;
  imapPort: number;
  imapUser: string;
  imapPassword: string;
  imapTls: boolean;
}

/**
 * Что возвращает `POST /mailboxes`. Плоский объект, не обёртка.
 *
 * Поле `folders` убрано 08.09: это было ТРЕТЬЕ объявление формы папки на
 * фронте, причём состояние типизировано как `string` — мимо словаря,
 * который для этого и выносили в `shared`. Никто его не читал (форма
 * добавления берёт из ответа только `id`), но оно переживало бы следующую
 * правку словаря молча.
 */
export interface CreatedMailbox {
  id: string;
  email: string;
  name: string;
  provider: string;
}

export interface SmtpPreset {
  host: string;
  port: number;
  securityMode: SmtpSecurityMode;
}

/**
 * Каталог SMTP-настроек по домену — берётся с бэкенда.
 *
 * Раньше фронт держал свою копию, и она разошлась: protonmail.com и pm.me
 * знал только бэкенд, поэтому автозаполнение для них не работало.
 * Источник теперь один — core/entities/mailbox.ts.
 */
export function useSmtpPresets(): UseQueryResult<Record<string, SmtpPreset>, Error> {
  return useQuery({
    queryKey: ['smtp-presets'],
    queryFn: () =>
      client
        .get<{ presets: Record<string, SmtpPreset> }>('/mailboxes/smtp-presets')
        .then((r) => r.data.presets),
    staleTime: Infinity,   // каталог меняется вместе с релизом, не в рантайме
  });
}

export function useMailboxes(): UseQueryResult<Mailbox[], Error> {
  return useQuery({
    queryKey: ['mailboxes'],
    queryFn: () =>
      client.get<{ mailboxes: Mailbox[] }>('/mailboxes').then((r) => r.data.mailboxes),
    /**
     * Пока хоть одна папка синхронизируется — опрашиваем.
     *
     * Без этого состояние замирало: `POST /mailboxes/:id/sync` только ставит
     * задачи в очередь и возвращается сразу, а рефетч в `onSuccess`
     * успевает раньше, чем воркер запишет `syncing`. Оператор видел
     * стомиллисекундный спиннер кнопки и то же состояние, что было, —
     * а вся правка про то, что человек видит (замечено ревью 08.09).
     *
     * Идиома проекта: так же устроено ожидание черновика в
     * `useDialogueProposals`. Опрос прекращается сам, когда папок в работе
     * не осталось.
     */
    refetchInterval: (query) =>
      query.state.data?.some((mailbox) =>
        mailbox.folders?.some((folder) => folder.syncStatus === 'syncing')
      )
        ? 3000
        : false,
  });
}

export function useCreateMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    // Ответ ПЛОСКИЙ, без обёртки `mailbox` — так отдаёт `POST /mailboxes`.
    // Тип врал, и из-за этого `result.data.mailbox?.id` был `undefined`:
    // настройки SMTP после подключения ящика молча не сохранялись,
    // а форма закрывалась как при успехе
    mutationFn: (data: CreateMailboxDto) =>
      client.post<CreatedMailbox>('/mailboxes', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailboxes'] }),
  });
}

/** Сотрудники организации для выбора владельца. Доступно владельцу и админу. */
export function useStaff(enabled: boolean, orgId?: string): UseQueryResult<StaffMember[], Error> {
  return useQuery({
    // Организация в ключе: без неё при смене сессии в списке на секунду
    // показались бы логины прежней организации — сервер назначение
    // отобьёт, но имена уже видны
    queryKey: ['mailbox-staff', orgId ?? 'unknown'],
    enabled,
    queryFn: () =>
      client.get<{ staff: StaffMember[] }>('/mailboxes/staff').then((r) => r.data.staff),
  });
}

/** Передать ящик другому менеджеру. Диалоги и черновики переезжают вместе с ним. */
export function useReassignMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mailboxId, ownerId }: { mailboxId: string; ownerId: string }) =>
      client.patch<{ ownerLogin: string }>(`/mailboxes/${mailboxId}/owner`, { ownerId }),
    onSuccess: () => {
      // Инбокс тоже меняется: диалоги уезжают вместе с ящиком
      void queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useSyncMailbox() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mailboxId: string) => client.post(`/mailboxes/${mailboxId}/sync`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      /*
       * Второй рефетч с задержкой — чтобы поймать момент, когда воркер
       * поставит папке `syncing`. Первый почти всегда приходит раньше:
       * маршрут отвечает сразу после постановки задач в очередь. Дальше
       * подхватывает опрос в `useMailboxes`, но начаться ему не с чего,
       * пока в данных нет ни одной папки в работе.
       */
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
      }, 2000);
    },
  });
}

// SMTP types
export type SmtpSecurityMode = 'starttls' | 'smtps' | 'plain';

export interface TestSmtpDto {
  host: string;
  port: number;
  securityMode: SmtpSecurityMode;
  user: string;
  password: string;
}

export interface TestSmtpResult {
  ok: boolean;
  server?: string;
  securityMode?: SmtpSecurityMode;
  auth?: string;
  error?: string;
}

export interface UpdateSmtpDto {
  smtpHost: string;
  smtpPort: number;
  smtpSecurityMode: SmtpSecurityMode;
  smtpUser: string;
  smtpPassword: string;
  displayName: string | null;
}

/**
 * Test SMTP connection (does NOT save credentials)
 */
export function useTestSmtp(mailboxId: string) {
  return useMutation({
    mutationFn: (data: TestSmtpDto) =>
      client.post<TestSmtpResult>(`/mailboxes/${mailboxId}/test-smtp`, data).then(r => r.data),
  });
}

/**
 * Update SMTP settings for a mailbox
 */
export function useUpdateSmtp(mailboxId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateSmtpDto) =>
      client.patch(`/mailboxes/${mailboxId}/smtp`, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mailboxes'] }),
  });
}
